import { FC, useMemo } from 'react';
import { Decimal } from '../../../../datastructures';
import Icon from '../../../shared/Icon';
import TooltipWrapper from '../../../shared/TooltipWrapper';
import Typography from '../../../shared/Typography';
import LendAndBorrowHealthFactorLabel from '../LendAndBorrowHealthFactorLabel';

import './LendAndBorrowActionHealthFactor.scss';
import { HEALTH_FACTOR_DANGER, HEALTH_FACTOR_WARNING } from '../../../../constants';
import isMobile from 'is-mobile';

interface ActionModalHealthFactorDataProps {
  healthFactor: Decimal | null;
  newHealthFactor?: Decimal | null;
  size?: 'normal' | 'medium' | 'large';
  showDot?: boolean;
  tooltip?: boolean;
}

const mobile = isMobile();

const LendAndBorrowActionHealthFactor: FC<ActionModalHealthFactorDataProps> = props => {
  const { healthFactor, newHealthFactor, size = 'normal', showDot = false, tooltip = true } = props;

  const noChange = useMemo(() => {
    if (!healthFactor || !newHealthFactor) {
      return true;
    }

    return healthFactor.equals(newHealthFactor);
  }, [healthFactor, newHealthFactor]);

  const dotColor = useMemo(() => {
    if (!healthFactor) {
      return '--text-primary';
    }

    if (healthFactor.lt(HEALTH_FACTOR_DANGER)) {
      return '--text-error';
    }

    if (healthFactor.lte(HEALTH_FACTOR_WARNING)) {
      return '--text-warning';
    }

    return '--text-success';
  }, [healthFactor]);

  const healthFactorTooltip = useMemo(() => {
    return (
      <div className="nostra__tooltipBox">
        <Typography variant="table-body-md" color="text-primary-darkbg">
          Your health factor reflects the value of your total collateral vs. the value of your total borrowings for this
          account.
        </Typography>
        <div className="nostra__lendAndBorrowActionHealthFactor__tooltipRow">
          <div
            style={{ background: 'var(--text-success)' }}
            className="nostra__lendAndBorrowActionHealthFactor__tooltipRowDot"
          />
          <Typography variant="table-body-md" color="text-primary-darkbg">
            Sufficient collateral
          </Typography>
        </div>
        <div className="nostra__lendAndBorrowActionHealthFactor__tooltipRow">
          <div
            style={{ background: 'var(--text-warning)' }}
            className="nostra__lendAndBorrowActionHealthFactor__tooltipRowDot"
          />
          <Typography variant="table-body-md" color="text-primary-darkbg">
            Add collateral or (partially) repay to avoid liquidation
          </Typography>
        </div>
        <div className="nostra__lendAndBorrowActionHealthFactor__tooltipRow">
          <div
            style={{ background: 'var(--text-error)' }}
            className="nostra__lendAndBorrowActionHealthFactor__tooltipRowDot"
          />
          <Typography variant="table-body-md" color="text-primary-darkbg">
            Imminent risk of liquidation
          </Typography>
        </div>
      </div>
    );
  }, []);

  return (
    <TooltipWrapper
      tooltipContent={healthFactorTooltip}
      placement="bottom-end"
      openEvent={mobile ? 'click' : 'mouseenter'}
      anchorClasses="nostra__lendAndBorrowActionHealthFactor__anchor"
      disabled={!tooltip}
    >
      <div className="nostra__lendAndBorrowActionHealthFactor">
        {showDot && healthFactor && (
          <div
            style={{ backgroundColor: `var(${dotColor})` }}
            className="nostra__lendAndBorrowActionHealthFactor__dot"
          />
        )}
        {noChange && <LendAndBorrowHealthFactorLabel healthFactor={healthFactor || null} size={size} />}
        {!noChange && (
          <>
            <LendAndBorrowHealthFactorLabel healthFactor={healthFactor || null} size={size} />
            <Icon variant="arrow-right" size={12} color="--icon-supporting" />
            <LendAndBorrowHealthFactorLabel healthFactor={newHealthFactor || null} size={size} />
          </>
        )}
      </div>
    </TooltipWrapper>
  );
};

export default LendAndBorrowActionHealthFactor;
